import { useTranslation } from 'react-i18next'

import {
  Card,
  CardBody,
  CardHeader,
  CardSubtitle,
  CardTitle,
} from '@/components/ui/Card'
import { DatePicker, Field, Input, Select } from '@/components/ui/Field'
import { Disclosure } from '@/components/ui/Disclosure'
import {
  useClientContacts,
  useClients,
} from '@/features/directories/api/useDirectory'
import { QuoteTeamCard } from '@/features/quotes/components/QuoteTeamCard'
import type { QuoteDraft } from '@/features/quotes/quoteDraft'
import { isSuppressed } from '@/types/domain'

/* The create form: who the offer is for, and what is moving.
 *
 * ── Why six questions and not twenty ─────────────────────────────────────────
 *
 * The step this replaces asked for the whole FR-4 header at once — the
 * Incoterm, the demurrage wording, the delivery terms, the carrier line, the
 * transit range and three measure overrides — on the screen a salesperson meets
 * before the quote exists. Almost
 * none of it is known at that moment, and a form that asks twenty questions to
 * record six teaches people to tab past nineteen of them.
 *
 * So the step asks the six a quote cannot be opened without: the client, the
 * person at the client, whose goods these are, what they are, the date and how
 * long the offer stands. NOTHING was dropped from the wire. Every field named
 * above is still submitted, on the step that owns its subject, behind a
 * disclosure — a field the form stops sending is a field the next whole-record
 * PUT silently empties, which is the failure toQuoteUpdatePayload exists to
 * prevent and which this step must not reintroduce from the other end.
 *
 * ── Why the team stays HERE, and the parties do not ──────────────────────────
 *
 * The team is behind the disclosure rather than on another step, and the reason
 * is transactional rather than aesthetic: step 1 IS the create, so a colleague
 * named here is written inside the create's own transaction. Moving them to a
 * later step would turn one atomic write into a second request that can fail on
 * its own, and membership is what grants visibility — a quote that existed for
 * a moment with nobody but its owner on it is a quote a colleague could not
 * open.
 *
 * The PARTIES are not asked for here at all, and that is a product decision
 * rather than a layout one: a quote does not name its external parties. They are
 * named on the CASE, which is where a shipper and a consignee are actually acted
 * on, and QuoteParticipantsSection on the detail page keeps the section for the
 * quotes that still need one before there is a case. Nothing about that section
 * or the /quotes/:id/participants endpoints changed — what left is the question
 * on the screen a salesperson meets before the quote exists.
 *
 * A grid rather than the sheet's single column, for the reason the step this
 * replaces gave: the step spans the page, and a lone input stretched across a
 * 1440px viewport is a line nobody can scan back along. One column on a phone,
 * two from `sm` up and no further — see the CardBody, where the pairing that
 * settles the number is spelled out.
 */

export function QuoteIdentityStep({
  draft,
  setDraft,
  touched,
}: {
  draft: QuoteDraft
  setDraft: (draft: QuoteDraft) => void
  /** Errors stay quiet until a submit has been attempted. */
  touched: boolean
}) {
  const { t } = useTranslation()
  // Searchable, so a directory of hundreds stays usable. The list is the one
  // this caller may see — Sales sees their own clients, and the server is what
  // decides that (AD-5).
  const { data: clients } = useClients()

  const clientError =
    touched && !draft.clientId ? t('common.form.required') : undefined

  /* The selected client's contacts (FR-4), from the DETAIL read — the client
     list this step already loads carries NONE, because directory.ListClients
     deliberately loads no contacts, and reading them from it left this picker
     empty however many the client had.

     In the directory's own order, because quote.firstAddressable picks the
     first addressable one and a re-sort here would make the hint name somebody
     the send would not address. */
  const contacts = useClientContacts(draft.clientId || undefined)
  const defaultContact = contacts.find(
    (contact) =>
      !isSuppressed(contact.email) && (contact.email ?? '').trim() !== '',
  )
  const contactOptions = contacts.map((contact) => ({
    value: contact.id,
    label: contact.label,
  }))
  const selectedContact = contacts.find(
    (contact) => contact.id === draft.clientContactId,
  )

  /* The phone and the email of the person the offer is addressed to, as words
     rather than as values. Both are Maybe on the wire, so both are read through
     isSuppressed: a role that may not see an address receives a marker object,
     and rendering it unguarded would print "[object Object]" where a colleague
     expects a redaction. */
  const contactChannels = selectedContact
    ? [
        isSuppressed(selectedContact.phone) ? undefined : selectedContact.phone,
        isSuppressed(selectedContact.email) ? undefined : selectedContact.email,
      ].filter((channel): channel is string => (channel ?? '').trim() !== '')
    : []

  return (
    <div className="flex flex-col gap-3.5">
      <Card>
        {/* The step's own name, above the card's. It replaces the icon that used
            to sit beside the title: a glyph says the step is about people, the
            eyebrow says WHICH of three steps this is — and with the rail now
            compact in the page header, that second fact had nowhere else to be
            stated in full. */}
        <CardHeader>
          {/* No step number any more: there is no second step to be first of.
              The card names what it asks for, and the page header names the
              act. */}
          <CardTitle className="text-[17px]">
            {t('quotes.create.identity')}
          </CardTitle>
          <CardSubtitle>{t('quotes.create.identityHint')}</CardSubtitle>
        </CardHeader>

        {/* Two columns, not three. The six questions pair off — the client and
            the person at it, whose goods and what they are, the date and how
            long it stands — and a three-column grid broke every one of those
            pairs across a row boundary. */}
        <CardBody className="grid gap-x-5 gap-y-4 pt-3 sm:grid-cols-2">
          <Field
            label={t('quotes.columns.client')}
            required
            error={clientError}
          >
            {(props) => (
              <Select
                value={draft.clientId}
                /* Cleared with the client: a contact id from the previous one
                   names a person at a different company. */
                onChange={(clientId) =>
                  setDraft({ ...draft, clientId, clientContactId: '' })
                }
                options={(clients ?? []).map((client) => ({
                  value: client.id,
                  label: client.name,
                }))}
                placeholder={t('common.form.selectPlaceholder')}
                searchable
                {...props}
              />
            )}
          </Field>

          {/* FR-4's "customer AND contact". A pick from the client's own
              contacts, never free text: the send refuses any address the
              directory does not hold. Blank is ordinary — the server then
              addresses the first contact carrying an email, and the hint names
              which that is rather than leaving it to be guessed. */}
          <div className="flex flex-col">
            <Field
              label={t('quotes.form.clientContact')}
              /* Only when there is something to SAY. "Optional" was the
                 fallback here, and on a form where three of six fields carried
                 it the word had stopped meaning anything — the required ones
                 are marked instead, which is one mark on the screen rather than
                 three. */
              hint={
                draft.clientContactId === '' && defaultContact
                  ? t('quotes.form.clientContactDefault', {
                      name: defaultContact.label,
                    })
                  : undefined
              }
            >
              {(props) => (
                <Select
                  value={draft.clientContactId}
                  onChange={(clientContactId) =>
                    setDraft({ ...draft, clientContactId })
                  }
                  options={contactOptions}
                  placeholder={t('common.form.selectPlaceholder')}
                  {...props}
                />
              )}
            </Field>

            {/* What the workbook prints under the customer: how to reach the
                person the offer is addressed to. Read-only, from the directory
                record — this is a FACT about the client, not a question for the
                salesperson, and giving it an input would invite a second copy of
                a phone number that already has one home (FR-39). */}
            {contactChannels.length > 0 ? (
              <p className="text-text-muted mt-1 text-[12px] leading-snug font-light">
                {contactChannels.join(' · ')}
              </p>
            ) : null}
          </div>

          {/* FR-4's header cargo — whose goods, and what they are. Not the FR-12
              positions, which are entered on the routing step and carry their own
              descriptions; this is the line that prints above that table. */}
          {/* A placeholder rather than an "Optional" hint, on both. The
              placeholder answers the question the hint did not — what a good
              answer looks like — and it lives INSIDE the box, so the two cargo
              fields no longer stand a line taller than the four beside them. */}
          <Field label={t('quotes.form.cargoOwner')}>
            {(props) => (
              <Input
                value={draft.cargoOwner}
                placeholder={t('quotes.form.cargoOwnerPlaceholder')}
                onChange={(e) => setDraft({ ...draft, cargoOwner: e.target.value })}
                {...props}
              />
            )}
          </Field>

          <Field label={t('quotes.form.cargoDescription')}>
            {(props) => (
              <Input
                value={draft.cargoDescription}
                placeholder={t('quotes.form.cargoDescriptionPlaceholder')}
                onChange={(e) =>
                  setDraft({ ...draft, cargoDescription: e.target.value })
                }
                {...props}
              />
            )}
          </Field>

          {/* The two dates are one thought — how long this offer stands — so
              they share a row. */}
          <Field label={t('quotes.form.quoteDate')} required>
            {(props) => (
              <DatePicker
                value={draft.quoteDate}
                onChange={(quoteDate) => setDraft({ ...draft, quoteDate })}
                {...props}
              />
            )}
          </Field>

          <Field
            label={t('quotes.columns.validUntil')}
            hint={t('quotes.form.validUntilHint')}
          >
            {(props) => (
              <DatePicker
                value={draft.validUntil}
                onChange={(validUntil) => setDraft({ ...draft, validUntil })}
                {...props}
              />
            )}
          </Field>
        </CardBody>
      </Card>

      {/* The team, shut by default. It is genuinely optional — most quotes are
          opened by one person — and it was on the first screen, which is how a
          form asking six questions came to look like one asking twenty.

          The count is ONE CARD rather than a field total, and that is the honest
          number here: what is behind the press is a list somebody adds people
          to, not a fixed set of inputs to fill in. It reads 1 rather than 2
          because the parties card that used to sit above this one is gone — and
          the count is inside the disclosure's accessible NAME, so leaving it at
          2 would not be an untidy label, it would be a screen reader announcing
          a card that is not there.

          Create-only by construction, and this form is now the create and
          nothing else. The card holds ids that ride in on the create payload,
          in the create's own transaction, and PUT /quotes/:id ignores the key —
          which toQuotePayload states where it omits it. On a saved quote the
          team is edited on its own card on the detail page, against endpoints
          that notify. */}
      <Disclosure summary={t('common.form.more')} count={1}>
        <div className="flex flex-col gap-3.5 sm:col-span-2 lg:col-span-3">
          <QuoteTeamCard draft={draft} setDraft={setDraft} />
        </div>
      </Disclosure>
    </div>
  )
}
